import { Plus, Trash2, PackageSearch, CalendarHeart, PencilLine } from "lucide-react";
import { money } from "@/lib/format";
import { BuscadorArticulo } from "@/components/BuscadorArticulo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export const lineaLibre = { concepto: "", monto_unitario: "", cantidad: 1, articulo_id: null,
                            combo_id: null, actividad_id: null, categoria: null, venta_id: null };

const totalLinea = (l) => Number(l.cantidad || 0) * Number(l.monto_unitario || 0);

/** Editor de las líneas de una factura: inventario, combos, actividades o conceptos libres. */
export const LineasFactura = ({ lineas, onChange, articulos = [], combos = [],
                                actividades = [] }) => {
  const set = (i, cambios) => onChange(lineas.map((l, j) => (j === i ? { ...l, ...cambios } : l)));
  const quitar = (i) => onChange(lineas.filter((_, j) => j !== i));
  const total = lineas.reduce((s, l) => s + totalLinea(l), 0);

  const elegirArticulo = (i, a) => set(i, {
    articulo_id: a.id, combo_id: null, actividad_id: null, categoria: a.categoria,
    concepto: `${a.categoria || "Artículo"}: ${a.nombre}${a.talla ? ` talla ${a.talla}` : ""}`,
    monto_unitario: String(a.precio),
  });

  const agregarCombo = (cid) => {
    const c = combos.find((x) => x.id === cid);
    if (!c) return;
    onChange([...lineas, { ...lineaLibre, combo_id: c.id, categoria: "Combo",
                           concepto: `Combo: ${c.nombre}`,
                           monto_unitario: String(c.precio_total) }]);
  };

  const agregarActividad = (aid) => {
    const a = actividades.find((x) => x.id === aid);
    if (!a) return;
    onChange([...lineas, { ...lineaLibre, actividad_id: a.id, categoria: "Actividad",
                           concepto: `Actividad: ${a.nombre}`,
                           monto_unitario: String(a.costo || 0) }]);
  };

  return (
    <div className="space-y-4" data-testid="lineas-factura">
      {lineas.map((l, i) => (
        <div key={i} className="rounded-md border border-border bg-muted/20 p-4"
             data-testid={`linea-factura-${i}`}>
          <div className="grid gap-3 sm:grid-cols-12">
            {articulos.length > 0 && !l.venta_id && (
              <div className="space-y-1 sm:col-span-12">
                <Label className="text-xs">Artículo del inventario (opcional)</Label>
                <BuscadorArticulo articulos={articulos} valor={l.articulo_id}
                                  testId={`articulo-factura-${i}`}
                                  onChange={(a) => elegirArticulo(i, a)} />
              </div>
            )}
            <div className="space-y-1 sm:col-span-7">
              <Label className="text-xs">Concepto</Label>
              <Input value={l.concepto} data-testid={`linea-factura-concepto-${i}`}
                     placeholder="Mensualidad, uniforme, libro, combo…"
                     onChange={(e) => set(i, { concepto: e.target.value })} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Cantidad</Label>
              <Input type="number" min="1" value={l.cantidad}
                     data-testid={`linea-factura-cantidad-${i}`}
                     onChange={(e) => set(i, { cantidad: e.target.value })} />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label className="text-xs">Precio (RD$)</Label>
              <Input type="number" value={l.monto_unitario}
                     data-testid={`linea-factura-precio-${i}`}
                     onChange={(e) => set(i, { monto_unitario: e.target.value })} />
            </div>
            <div className="flex items-end sm:col-span-1">
              <Button size="sm" variant="ghost" data-testid={`quitar-linea-factura-${i}`}
                      disabled={lineas.length === 1} onClick={() => quitar(i)}>
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs">
            <span className="text-muted-foreground">
              {l.venta_id ? "Viene de una venta registrada"
                : l.articulo_id ? "Artículo del inventario · descuenta existencia"
                : l.combo_id ? "Combo del catálogo"
                : l.actividad_id ? "Actividad del catálogo" : "Concepto libre"}
            </span>
            <span className="font-mono font-semibold">{money(totalLinea(l))}</span>
          </div>
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-full"
                data-testid="agregar-linea-factura"
                onClick={() => onChange([...lineas, { ...lineaLibre }])}>
          <Plus className="mr-2 h-4 w-4" /> Concepto libre
        </Button>
        {articulos.length > 0 && (
          <span className="flex items-center gap-2 text-xs text-muted-foreground">
            <PackageSearch className="h-3.5 w-3.5 text-primary" />
            Use el buscador de cada línea para uniformes, libros y materiales
          </span>
        )}
        {combos.length > 0 && (
          <select className="rounded-full border border-border bg-background px-4 py-2 text-xs"
                  value="" data-testid="agregar-linea-combo"
                  onChange={(e) => agregarCombo(e.target.value)}>
            <option value="">Agregar combo del catálogo…</option>
            {combos.map((c) => (
              <option key={c.id} value={c.id}>{`${c.nombre} · ${money(c.precio_total)}`}</option>
            ))}
          </select>
        )}
        {actividades.length > 0 && (
          <select className="rounded-full border border-border bg-background px-4 py-2 text-xs"
                  value="" data-testid="agregar-linea-actividad"
                  onChange={(e) => agregarActividad(e.target.value)}>
            <option value="">Agregar actividad…</option>
            {actividades.map((a) => (
              <option key={a.id} value={a.id}>{`${a.nombre} · ${money(a.costo)}`}</option>
            ))}
          </select>
        )}
        <span className="ml-auto flex items-center gap-2 font-display text-lg font-bold"
              data-testid="total-lineas-factura">
          <PencilLine className="h-4 w-4 text-primary" /> {money(total)}
        </span>
      </div>
    </div>
  );
};

export const CataloguitoCombos = CalendarHeart;
